import {
  getMiddlePageScopedField,
  isMiddlePage,
} from '@/views/wheelOfFortune/pop/utils/stepPages';

export type PageKey =
  | 'start'
  | 'middle'
  | 'middle2'
  | 'middle3'
  | 'middle4'
  | 'end'
  | 'cover'
  | 'packUp';

export type StyleElement =
  | 'title'
  | 'subtitle'
  | 'activity'
  | 'thanks'
  | 'button'
  | 'show'
  | 'wheelContainer'
  | 'privacy'
  | 'privacyCheck'
  | 'couponPrompt'
  | 'input';

type StyleKV = Record<string, string>;

/**
 * styleMap key 逐元素表驱动：end 页后缀有 -end 与 __end 两种历史约定，
 * 不得「统一规律」——类名即线上 styleMap key，改动会使存量配置失效。
 */
const STYLE_KEY_RULES: Record<
  StyleElement,
  { base: string; endSuffix: '-end' | '__end'; cover?: string; endKeepsBase?: boolean }
> = {
  title: { base: 'cartx-b-title', endSuffix: '-end' },
  subtitle: { base: 'cartx-b-subtitle', endSuffix: '-end' },
  activity: { base: 'cartx-b-activity', endSuffix: '-end' },
  thanks: { base: 'cartx-b-thanks', endSuffix: '-end' },
  button: { base: 'cartx-b-button', endSuffix: '-end' },
  show: { base: 'cartx-b-show', endSuffix: '__end', cover: 'cartx-b-show__cover' },
  wheelContainer: { base: 'cartx-b-wheel-container', endSuffix: '__end' },
  privacy: { base: 'cartx-b-privacy', endSuffix: '-end' },
  privacyCheck: { base: 'cartx-b-privacy-check', endSuffix: '-end' },
  // end 页复用 start key（textEndNew.vue 现状，不得以「找规律」名义加后缀）
  couponPrompt: { base: 'cartx-b-coupon-prompt', endSuffix: '-end', endKeepsBase: true },
  input: { base: 'cartx-b-input', endSuffix: '-end' },
};

export function styleKey(element: StyleElement, page: PageKey = 'start'): string {
  const rule = STYLE_KEY_RULES[element];
  if (page === 'end') {
    return rule.endKeepsBase ? rule.base : `${rule.base}${rule.endSuffix}`;
  }
  if (page === 'cover') {
    return rule.cover ?? rule.base;
  }
  if (isMiddlePage(page)) {
    return `${rule.base}__${page}`;
  }
  return rule.base;
}

/**
 * 读取某元素在某页的样式，回退链忠实 popNew.vue getScopedStyleMap：
 * middleN → __middleN ?? __middle ?? {}；其他页只读自身 key，不跨页回退。
 */
export function readStyle(
  styleMap: Record<string, StyleKV> | undefined,
  element: StyleElement,
  page: PageKey = 'start'
): StyleKV {
  const map = styleMap || {};
  if (isMiddlePage(page)) {
    return map[styleKey(element, page)] || map[styleKey(element, 'middle')] || {};
  }
  return map[styleKey(element, page)] || {};
}

export type FlatField =
  | 'imgUrl'
  | 'bgImgUrl'
  | 'imgPosition'
  | 'bgbox'
  | 'buttonAction'
  | 'openUrl'
  | 'closeColor'
  | 'closeButtonConfig'
  | 'mobilePictureShow'
  | 'countDownShow'
  | 'privacyPosition';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * 扁平字段名（rules/typesetting 分组内，分组由调用方确定）。
 * end 页：end + Capitalized，例外 imgUrl → imgUrlEnd（历史词序相反）。
 * middle 页：middle + Capitalized，经 getMiddlePageScopedField 改写 middleN。
 * cover 页字段在 settings.cover.* 嵌套分组，不在此函数范围。
 */
export function fieldKey(field: FlatField, page: PageKey = 'start'): string {
  if (page === 'end') {
    if (field === 'imgUrl') {
      return 'imgUrlEnd';
    }
    return `end${capitalize(field)}`;
  }
  if (isMiddlePage(page)) {
    return getMiddlePageScopedField(page, `middle${capitalize(field)}`);
  }
  return field;
}
